/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faBriefcase, faGraduationCap, faBuilding, faUniversity, faUsers, faHandsHelping } from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../config/api';
import '../styles/Login.css';

const OAuthRoleSelection = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [oauthData, setOauthData] = useState(null);

  useEffect(() => {
    // Parse OAuth data from URL query parameter
    const dataParam = searchParams.get('data');
    if (dataParam) {
      try {
        const decoded = JSON.parse(decodeURIComponent(dataParam));
        setOauthData(decoded);
      } catch (e) {
        console.error('Error parsing OAuth data:', e);
        setError('Invalid OAuth data. Please try signing in again.');
      }
    } else {
      setError('No OAuth data found. Please try signing in again.');
    }
  }, [searchParams]);

  const handleRoleSelection = async (userType) => {
    if (!oauthData) {
      setError('OAuth data is missing. Please try again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(buildApiUrl('/api/auth/oauth/signup'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: oauthData.email,
          first_name: oauthData.first_name || oauthData.firstName || '',
          last_name: oauthData.last_name || oauthData.lastName || '',
          profile_picture: oauthData.profile_picture || oauthData.profileImage || '',
          google_id: oauthData.google_id || oauthData.googleId || '',
          provider: oauthData.provider || 'google',
          userType: userType
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store token and user data
        if (data.token) {
          localStorage.setItem('token', data.token);
        }
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
        }

        // Redirect to appropriate dashboard based on user type
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      } else {
        setError(data.message || 'Failed to complete signup. Please try again.');
      }
    } catch (e) {
      console.error('OAuth signup error:', e);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const roleOptions = [
    {
      type: 'jobSeeker',
      title: 'Job Seeker',
      icon: faUser,
      description: 'Looking for employment opportunities',
      color: '#3b82f6'
    },
    {
      type: 'recruiter',
      title: 'Recruiter / Employer',
      icon: faBriefcase,
      description: 'Post jobs and find talent',
      color: '#10b981'
    },
    {
      type: 'intern',
      title: 'Intern',
      icon: faGraduationCap,
      description: 'Seeking internship opportunities',
      color: '#f59e0b'
    },
    {
      type: 'university',
      title: 'University',
      icon: faUniversity,
      description: 'Manage student placements and internships',
      color: '#8b5cf6'
    },
    {
      type: 'association',
      title: 'Association',
      icon: faUsers,
      description: 'Manage member services',
      color: '#ec4899'
    },
    {
      type: 'ngo',
      title: 'NGO',
      icon: faHandsHelping,
      description: 'Hire talent and volunteers',
      color: '#06b6d4'
    }
  ];

  if (error && !oauthData) {
    return (
      <div className="login-container" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="login-box" style={{ textAlign: 'center', maxWidth: '500px' }}>
          <h2 style={{ color: '#e74c3c', marginBottom: '20px' }}>Error</h2>
          <p style={{ marginBottom: '20px' }}>{error}</p>
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '12px 24px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div className="login-box" style={{ maxWidth: '800px', width: '100%' }}>
        <h2 style={{ textAlign: 'center', marginBottom: '10px' }}>Complete Your Signup</h2>
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '30px' }}>
          Welcome! Please select your account type to continue.
        </p>

        {error && (
          <div style={{
            padding: '12px',
            background: '#fee',
            border: '1px solid #fcc',
            borderRadius: '6px',
            color: '#c33',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginTop: '30px'
        }}>
          {roleOptions.map((role) => (
            <button
              key={role.type}
              onClick={() => handleRoleSelection(role.type)}
              disabled={loading}
              style={{
                padding: '24px',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                background: 'white',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
                opacity: loading ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.borderColor = role.color;
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = `0 4px 12px ${role.color}33`;
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <FontAwesomeIcon
                icon={role.icon}
                style={{ fontSize: '32px', color: role.color }}
              />
              <div>
                <h3 style={{ margin: '0 0 8px 0', color: '#1f2937' }}>{role.title}</h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                  {role.description}
                </p>
              </div>
            </button>
          ))}
        </div>

        {loading && (
          <div style={{ textAlign: 'center', marginTop: '20px', color: '#666' }}>
            Creating your account...
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: '30px' }}>
          <button
            onClick={() => navigate('/login')}
            style={{
              background: 'none',
              border: 'none',
              color: '#3b82f6',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: '14px'
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default OAuthRoleSelection;
